'use client';

import { useEffect, useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { trackUserAction } from '@/app/actions/posthog';
import { PostHogEventName } from '@/types/posthog';
import { createClient } from '@/utils/supabase/client';
import { useOnboardingStore } from '../../store';
import { generateOnboardingForm, sendOnboardingFormEmail, resendConfirmationEmail } from '../../actions';
import { StepWrapper } from '../shared/StepWrapper';
import { ProgressIndicator } from '../shared/ProgressIndicator';

interface PreviewStepProps {
  onFinish: (action: 'customize' | 'scratch' | 'skip') => void;
  onBack: () => void;
  finishing?: boolean;
  currentStep?: number;
  totalSteps?: number;
  onReveal?: () => void;
}

function getFormTypeLabel(useCases: string[]): string {
  if (useCases.includes('collect_feedback')) return 'a post-purchase survey';
  if (useCases.includes('convert_customers')) return 'a product quiz';
  if (useCases.includes('improve_segmentation')) return 'a preference center';
  return 'an interactive form';
}

function getPreviewContent(useCases: string[], brandName: string) {
  const name = brandName || 'your brand';

  // Priority order must match generateOnboardingForm in actions.ts
  if (useCases.includes('collect_feedback')) {
    return {
      heading: `Here's a survey for ${name}`,
      subheading: '5 questions, ready to drop into your next email.',
    };
  }
  if (useCases.includes('convert_customers')) {
    return {
      heading: 'We built you a product quiz',
      subheading: `Based on ${name}'s products. Edit it, launch it, or start fresh.`,
    };
  }
  if (useCases.includes('improve_segmentation')) {
    return {
      heading: `Here's a preference center for ${name}`,
      subheading: 'Let subscribers tell you what they want. Responses sync to Klaviyo profiles automatically.',
    };
  }
  if (useCases.length > 1) {
    return {
      heading: 'We made you something to start with',
      subheading: `Based on what you told us, here's a template for ${name}. You can build more from the dashboard.`,
    };
  }
  return {
    heading: `Here's an interactive form for ${name}`,
    subheading: 'A quick template to see what Kinetic can do. Edit anything — make it yours.',
  };
}

export function PreviewStep({ onFinish, finishing, currentStep, totalSteps, onReveal }: PreviewStepProps) {
  const store = useOnboardingStore();
  const [pendingAction, setPendingAction] = useState<'customize' | 'scratch' | 'skip' | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(true);

  // After email confirmed AND form ready, keep overlay for 3 seconds then reveal
  useEffect(() => {
    if (emailConfirmed && showRevealOverlay && store.generatedFormId) {
      const timer = setTimeout(() => setShowRevealOverlay(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [emailConfirmed, showRevealOverlay, store.generatedFormId]);

  const handleResendCode = useCallback(async () => {
    if (!store.email || resending) return;
    setResending(true);
    await resendConfirmationEmail(store.email);
    setResending(false);
    setResent(true);
    setOtpCode('');
    setOtpError(null);
    setTimeout(() => setResent(false), 5000);
  }, [store.email, resending]);

  // Check email confirmation status on mount
  useEffect(() => {
    if (store.isAuthenticated) {
      const supabase = createClient();
      supabase.auth.getSession().then(({ data }) => {
        setEmailConfirmed(!!data.session);
      });
    }
  }, [store.isAuthenticated]);

  const content = getPreviewContent(store.useCases, store.brandName);
  const isLoading = store.aiGenerationStatus === 'extracting' || store.aiGenerationStatus === 'generating';
  const noGeneration = store.aiGenerationStatus === 'idle' && !store.generatedFormId;

  // If we have store data but no form yet, trigger generation
  useEffect(() => {
    if (
      store.storeData &&
      store.useCases.length > 0 &&
      store.companyId &&
      store.userId &&
      store.aiGenerationStatus === 'generating' &&
      !store.generatedFormId
    ) {
      generateOnboardingForm(
        store.companyId,
        store.userId,
        store.storeData,
        store.useCases
      ).then((result) => {
        if (result.success) {
          store.setGeneratedFormId(result.formId);
          store.setAiGenerationStatus('done');
          if (store.companyId) {
            trackUserAction(
              store.email,
              store.companyId,
              PostHogEventName.ONBOARDING_WIZARD_AI_FORM_GENERATED,
              { form_id: result.formId, form_type: result.formType }
            );
          }
        } else {
          store.setAiGenerationStatus('error');
          store.setAiGenerationError(result.error);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.storeData, store.aiGenerationStatus, store.generatedFormId, store.companyId, store.userId]);


  // Check email confirmation before navigating away
  const handleAction = useCallback(async (action: 'customize' | 'scratch' | 'skip') => {
    if (emailConfirmed) {
      onFinish(action);
      return;
    }

    // Re-check in case they confirmed via link in another tab
    const supabase = createClient();
    const { data } = await supabase.auth.getSession();

    if (data.session) {
      setEmailConfirmed(true);
      onFinish(action);
      return;
    }

    // Still not confirmed — store the intended action
    setPendingAction(action);
  }, [onFinish, emailConfirmed]);

  // Verify OTP code to confirm email + create session
  const handleVerifyCode = useCallback(async (code: string) => {
    if (!code || code.length < 6 || !store.email) return;

    setVerifying(true);
    setOtpError(null);

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.verifyOtp({
        email: store.email,
        token: code,
        type: 'email',
      });

      if (error) {
        setOtpError(error.message === 'Token has expired or is invalid'
          ? 'Invalid or expired code. Please check and try again.'
          : error.message
        );
        setVerifying(false);
        return;
      }

      // Success — email confirmed and session created
      setEmailConfirmed(true);

      // Track email confirmation in PostHog
      if (store.companyId) {
        trackUserAction(
          store.email,
          store.companyId,
          PostHogEventName.SIGNUP_FLOW_EMAIL_CONFIRMED,
          { confirmed_at: new Date().toISOString() }
        );
      }

      if (pendingAction) {
        onFinish(pendingAction);
      }
    } catch {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setVerifying(false);
    }
  }, [store.email, pendingAction, onFinish]);

  // No shop URL was provided — show confirmation or success
  if (noGeneration) {
    return (
      <StepWrapper
        title={emailConfirmed ? "You're all set!" : "Confirm your email"}
        subtitle={emailConfirmed
          ? "Head to the dashboard to create your first form."
          : `We sent a 6-digit code to ${store.email}`
        }
      >
        <div className="space-y-4">
          {/* Email confirmation OTP */}
          {!emailConfirmed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center gap-3 py-2"
            >
              {/* Desktop: show email info (StepWrapper hides title on lg) */}
              <div className="hidden lg:block text-center space-y-1 pb-2">
                <p className="text-sm text-muted-foreground">We sent a 6-digit code to</p>
                <p className="text-sm font-medium">{store.email}</p>
              </div>
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={(value) => {
                  setOtpCode(value);
                  setOtpError(null);
                  if (value.length === 6) handleVerifyCode(value);
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-lg" />
                  <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-lg" />
                  <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-lg" />
                  <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-lg" />
                  <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-lg" />
                  <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-lg" />
                </InputOTPGroup>
              </InputOTP>
              {verifying && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying...
                </div>
              )}
              {otpError && <p className="text-xs text-red-500">{otpError}</p>}
            </motion.div>
          )}

          {emailConfirmed && (
            <>
              {/* Desktop: show title (StepWrapper hides it on lg) */}
              <div className="hidden lg:block text-center space-y-1 pb-2">
                <h1 className="text-2xl font-bold tracking-tight">You're all set!</h1>
                <p className="text-sm text-muted-foreground">Head to the dashboard to create your first form.</p>
              </div>

              {/* Show AI-generated form preview if available */}
              {store.generatedFormId && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-[13px] border overflow-hidden bg-white shadow-sm"
                >
                  <iframe
                    src={`/forms/${store.generatedFormId}?preview=true`}
                    className="w-full h-[500px] border-0"
                    title="Form Preview"
                  />
                </motion.div>
              )}

              {store.generatedFormId ? (
                <>
                  <Button
                    onClick={() => handleAction('customize')}
                    className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
                    disabled={finishing}
                  >
                    {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Customize your form'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full rounded-[13px] h-10"
                    disabled={sendingEmail || emailSent}
                    onClick={async () => {
                      setSendingEmail(true);
                      await sendOnboardingFormEmail(
                        store.generatedFormId!,
                        store.email,
                        store.brandName,
                        store.storeData?.customDomain || store.storeData?.shopifyDomain || '',
                        store.storeData ? 'product_finder' : '',
                      );
                      setSendingEmail(false);
                      setEmailSent(true);
                    }}
                  >
                    {sendingEmail ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                    ) : emailSent ? (
                      <><Mail className="h-4 w-4 mr-2" /> Sent to {store.email}</>
                    ) : (
                      <><Mail className="h-4 w-4 mr-2" /> Send to my email</>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => handleAction('scratch')}
                  className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
                  disabled={finishing}
                >
                  {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create my first form'}
                </Button>
              )}
              <button
                onClick={() => handleAction('skip')}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Take me to the dashboard
              </button>
            </>
          )}
        </div>
      </StepWrapper>
    );
  }

  // Title/subtitle based on email confirmation state
  const firstName = store.firstName && !store.firstName.includes('@') ? store.firstName : '';
  const title = emailConfirmed
    ? `You're ready to go${firstName ? `, ${firstName}` : ''}!`
    : 'Confirm your email';
  const subtitle = emailConfirmed
    ? (store.generatedFormId
      ? "We built you a form that works inside emails and on your website."
      : "Your account is ready. Here's what you can do next.")
    : `We sent a 6-digit code to ${store.email}`;

  const formReady = !!store.generatedFormId;

  const sendEmailButton = (
    <Button
      variant="outline"
      size="sm"
      className="rounded-[10px] text-xs h-8 px-3 flex-shrink-0"
      disabled={!formReady || sendingEmail || emailSent}
      title={!formReady ? 'Generating your form...' : undefined}
      onClick={async () => {
        setSendingEmail(true);
        await sendOnboardingFormEmail(
          store.generatedFormId!,
          store.email,
          store.brandName,
          store.storeData?.customDomain || store.storeData?.shopifyDomain || '',
          store.storeData ? 'product_finder' : '',
        );
        setSendingEmail(false);
        setEmailSent(true);
      }}
    >
      {sendingEmail ? (
        <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Sending...</>
      ) : emailSent ? (
        <><Mail className="h-3.5 w-3.5 mr-1.5" /> Sent!</>
      ) : (
        <><Mail className="h-3.5 w-3.5 mr-1.5" /> Send email</>
      )}
    </Button>
  );

  const isGenerating = store.aiGenerationStatus === 'generating' || store.aiGenerationStatus === 'extracting';

  // Notify parent to switch to split layout when confirmed
  useEffect(() => {
    if (emailConfirmed && onReveal) onReveal();
  }, [emailConfirmed, onReveal]);

  // Before email confirmed: use standard layout (same as other steps)
  if (!emailConfirmed) {
    return (
      <StepWrapper
        title="Confirm your email"
        subtitle={`We sent a 6-digit code to ${store.email}`}
      >
        <div className="space-y-4">
          {/* Always show email info (StepWrapper hides title on desktop) */}
          <div className="hidden lg:block text-center space-y-1">
            <p className="text-sm text-muted-foreground">We sent a 6-digit code to</p>
            <p className="text-sm font-medium">{store.email}</p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-3 py-2"
          >
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={(value) => {
                setOtpCode(value);
                setOtpError(null);
                if (value.length === 6) handleVerifyCode(value);
              }}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-lg" />
              </InputOTPGroup>
            </InputOTP>
            {verifying && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying...
              </div>
            )}
            {otpError && <p className="text-xs text-red-500">{otpError}</p>}
            <button
              onClick={handleResendCode}
              disabled={resending}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {resending ? 'Sending...' : resent ? 'Code sent!' : "Didn't get it? Resend code"}
            </button>
          </motion.div>
        </div>
      </StepWrapper>
    );
  }

  if (store.generatedFormId || isGenerating) {
    return (
      <div className="flex min-h-svh">
        {/* Left: Form preview with reveal overlay */}
        <div className="hidden lg:flex lg:w-2/5 border-r h-svh sticky top-0 relative overflow-hidden">
          {/* Form iframe (loads behind the overlay) */}
          {store.generatedFormId && (
            <iframe
              src={`/forms/${store.generatedFormId}?preview=true`}
              className="w-full h-full border-0 bg-white"
              title="Form Preview"
            />
          )}

          {/* Reveal overlay — slides out after 3 seconds */}
          <motion.div
            className="absolute inset-0 bg-primary flex flex-col items-center justify-center p-10 z-10"
            initial={{ x: 0 }}
            animate={{ x: showRevealOverlay ? 0 : '-100%' }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="flex flex-col items-center gap-8 text-center max-w-xs">
              {/* Logo */}
              <div>
                <img src="/images/kinetic-logo-full.svg" alt="Kinetic" className="h-16 brightness-0 invert opacity-80" />
              </div>

              <h2 className="text-xl font-semibold text-primary-foreground flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
                Creating {getFormTypeLabel(store.useCases)} for {store.brandName || 'you'}
              </h2>
            </div>
          </motion.div>
        </div>

        {/* Right: Content */}
        <div className="w-full lg:w-3/5 flex flex-col">
          {currentStep !== undefined && totalSteps !== undefined && (
            <div className="pt-6 px-8">
              <ProgressIndicator currentStep={currentStep} totalSteps={totalSteps} />
            </div>
          )}
          <div className="flex-1 flex items-center justify-center p-8 md:p-12">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
              <p className="text-muted-foreground text-sm md:text-base">
                {emailConfirmed && store.generatedFormId
                  ? <>We built you a form that works inside emails<br />and on your website.</>
                  : subtitle}
              </p>
            </div>

            {/* OTP input — when not confirmed */}
            {!emailConfirmed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-3"
              >
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => {
                    setOtpCode(value);
                    setOtpError(null);
                    if (value.length === 6) handleVerifyCode(value);
                  }}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-lg" />
                    <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-lg" />
                    <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-lg" />
                    <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-lg" />
                    <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-lg" />
                    <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-lg" />
                  </InputOTPGroup>
                </InputOTP>
                {verifying && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying...
                  </div>
                )}
                {otpError && <p className="text-xs text-red-500">{otpError}</p>}
                <button
                  onClick={handleResendCode}
                  disabled={resending}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {resending ? 'Sending...' : resent ? 'Code sent!' : "Didn't get it? Resend code"}
                </button>
              </motion.div>
            )}

            {/* Mobile form preview (hidden on desktop where it's on the left) */}
            {emailConfirmed && (
              <div className="lg:hidden rounded-[13px] border overflow-hidden bg-white shadow-sm">
                <iframe
                  src={`/forms/${store.generatedFormId}?preview=true`}
                  className="w-full h-[400px] border-0"
                  title="Form Preview"
                />
              </div>
            )}

            {/* Next steps — shown after confirmed */}
            {emailConfirmed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-5"
              >
                <h3 className="text-sm font-semibold">Next steps:</h3>

                {/* Step 1: Send email */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">1</span>
                    <span className="text-sm whitespace-nowrap">Send it to yourself</span>
                  </div>
                  {sendEmailButton}
                </div>

                {/* Step 2: Customize */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">2</span>
                    <span className="text-sm">Customize this form</span>
                  </div>
                  <div className="ml-9 space-y-2">
                    <Button
                      onClick={() => handleAction('customize')}
                      className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-11 text-sm"
                      disabled={finishing || !formReady}
                      title={!formReady ? 'Generating your form...' : undefined}
                    >
                      {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : !formReady ? 'Generating...' : 'Customize in editor'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Or: <button
                        onClick={() => handleAction('scratch')}
                        className="underline hover:text-foreground transition-colors"
                        disabled={finishing}
                      >start from scratch</button>
                    </p>
                  </div>
                </div>

                <div className="text-right pt-4">
                  <button
                    onClick={() => handleAction('skip')}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Skip — go to dashboard
                  </button>
                </div>
              </motion.div>
            )}
          </div>
          </div>
        </div>
      </div>
    );
  }

  // No form — standard centered layout
  return (
    <StepWrapper title={title} subtitle={subtitle}>
      <div className="space-y-5">
        {/* Email confirmation OTP — shown when not confirmed */}
        {!emailConfirmed && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-3 py-2"
          >
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={(value) => {
                setOtpCode(value);
                setOtpError(null);
                if (value.length === 6) handleVerifyCode(value);
              }}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-lg" />
                <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-lg" />
              </InputOTPGroup>
            </InputOTP>
            {verifying && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying...
              </div>
            )}
            {otpError && <p className="text-xs text-red-500">{otpError}</p>}
            <button
              onClick={handleResendCode}
              disabled={resending}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {resending ? 'Sending...' : resent ? 'Code sent!' : "Didn't get it? Resend code"}
            </button>
          </motion.div>
        )}

        {/* Confirmed but no form generated */}
        {emailConfirmed && (
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => handleAction('scratch')}
              className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
              disabled={finishing}
            >
              {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create my first form'}
            </Button>
            <button
              onClick={() => handleAction('skip')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
            >
              Take me to the dashboard
            </button>
          </div>
        )}
      </div>
    </StepWrapper>
  );
}
